import { useState, useMemo } from 'react'
import { TextField as MuiTextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip } from '@mui/material'
import type { FieldMeta } from '@crm/shared'

interface JsonFieldProps {
  field: FieldMeta
  value: unknown
  mode: 'view' | 'edit'
  onChange?: (value: unknown) => void
  error?: string
  disabled?: boolean
  hideLabel?: boolean
}

function tryParseJson(val: unknown): { parsed: unknown; raw: string } | null {
  if (val == null) return null
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val)
      if (typeof parsed === 'object' && parsed !== null) {
        return { parsed, raw: val }
      }
      return null
    } catch {
      return null
    }
  }
  if (typeof val === 'object') {
    return { parsed: val, raw: JSON.stringify(val) }
  }
  return null
}

function highlightJson(json: string): React.ReactNode[] {
  const lines = json.split('\n')
  return lines.map((line, lineIdx) => {
    const parts: React.ReactNode[] = []
    let keyIdx = 0

    const keyMatch = line.match(/^(\s*)"([^"]+)"(\s*:\s*)/)
    if (keyMatch) {
      parts.push(
        <span key={`indent-${keyIdx}`}>{keyMatch[1]}</span>,
        <span key={`key-${keyIdx}`} style={{ color: '#0b8084', fontWeight: 600 }}>"{keyMatch[2]}"</span>,
        <span key={`colon-${keyIdx}`}>{keyMatch[3]}</span>,
      )
      keyIdx++
      const rest = line.slice(keyMatch[0].length)
      parts.push(...highlightValue(rest, `val-${lineIdx}`))
    } else {
      const bracketMatch = line.match(/^(\s*)([\[\]{}],?\s*)$/)
      if (bracketMatch) {
        parts.push(
          <span key={`indent-${lineIdx}`}>{bracketMatch[1]}</span>,
          <span key={`bracket-${lineIdx}`} style={{ color: '#666' }}>{bracketMatch[2]}</span>,
        )
      } else {
        parts.push(<span key={`line-${lineIdx}`}>{line}</span>)
      }
    }

    if (lineIdx < lines.length - 1) {
      parts.push(<span key={`nl-${lineIdx}`}>{'\n'}</span>)
    }

    return <span key={`line-${lineIdx}`}>{parts}</span>
  })
}

function highlightValue(val: string, prefix: string): React.ReactNode[] {
  const trimmed = val.replace(/,\s*$/, '')
  const trailingComma = val.endsWith(',') ? ',' : ''

  const parts: React.ReactNode[] = []

  if (trimmed.startsWith('"')) {
    parts.push(<span key={`${prefix}-str`} style={{ color: '#c41a16' }}>{trimmed}</span>)
  } else if (trimmed === 'true' || trimmed === 'false') {
    parts.push(<span key={`${prefix}-bool`} style={{ color: '#6f42c1' }}>{trimmed}</span>)
  } else if (trimmed === 'null') {
    parts.push(<span key={`${prefix}-null`} style={{ color: '#6f42c1' }}>{trimmed}</span>)
  } else if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(trimmed)) {
    parts.push(<span key={`${prefix}-num`} style={{ color: '#005cc5' }}>{trimmed}</span>)
  } else if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    parts.push(<span key={`${prefix}-bracket`} style={{ color: '#666' }}>{trimmed}</span>)
  } else {
    parts.push(<span key={`${prefix}-other`}>{trimmed}</span>)
  }

  if (trailingComma) {
    parts.push(<span key={`${prefix}-comma`}>{trailingComma}</span>)
  }

  return parts
}

export function JsonField({ field, value, mode, onChange, error, disabled, hideLabel }: JsonFieldProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editError, setEditError] = useState('')

  const highlightedNodes = useMemo(() => {
    if (!dialogOpen) return null
    try {
      const obj = typeof value === 'string' ? JSON.parse(value) : value
      const beautified = JSON.stringify(obj, null, 2)
      return highlightJson(beautified)
    } catch {
      return null
    }
  }, [dialogOpen, value])

  if (mode === 'view') {
    const parsed = tryParseJson(value)

    if (!parsed) {
      if (value == null || value === '') return <span />
      return <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{String(value)}</span>
    }

    const preview = parsed.raw.length > 80 ? parsed.raw.slice(0, 80) + '...' : parsed.raw

    return (
      <>
        <Box
          onClick={() => setDialogOpen(true)}
          sx={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
            px: 0.5,
            py: 0.25,
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <Chip label="JSON" size="small" variant="outlined" sx={{ fontSize: 10, height: 20, flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {preview}
          </Typography>
        </Box>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {field.label}
            <Chip label="JSON" size="small" variant="outlined" sx={{ fontSize: 11 }} />
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                bgcolor: '#fafafa',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                overflow: 'auto',
                maxHeight: 500,
              }}
            >
              <pre style={{ margin: 0, fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {highlightedNodes}
              </pre>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  const stringValue = typeof value === 'string' ? value : value != null ? JSON.stringify(value, null, 2) : ''

  return (
    <>
      <MuiTextField
        label={hideLabel ? undefined : field.label}
        value={stringValue}
        onChange={(e) => {
          const v = e.target.value
          onChange?.(v)
          if (v.trim()) {
            try {
              const parsed = JSON.parse(v)
              if (typeof parsed !== 'object' || parsed === null) {
                setEditError('Must be a JSON object or array')
              } else {
                setEditError('')
              }
            } catch {
              setEditError('Invalid JSON')
            }
          } else {
            setEditError('')
          }
        }}
        error={!!error || !!editError}
        helperText={error || editError}
        disabled={disabled}
        required={!!field.is_required}
        size="small"
        fullWidth
        multiline
        minRows={3}
        maxRows={8}
        slotProps={{ htmlInput: { maxLength: 50000, style: { fontFamily: 'monospace', fontSize: 12 } } }}
      />
    </>
  )
}
